import React, { useEffect, useState } from "react";
import { Check, Circle, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useApp } from "@/context/AppContext";
import { ProfileCompletionSheet, useCompletionLabels } from "@/components/ProfileCompletionSheet";
import { PROFILE_DONE_DISMISS_KEY as DISMISS_KEY, profileIncomplete } from "@/lib/profileDone";
import { holdNativePrompts, releaseNativePrompts } from "@/lib/nativePrompts";

const Ring = ({ percent }) => {
  const r = 26, c = 2 * Math.PI * r;
  return (
    <svg width="68" height="68" viewBox="0 0 68 68" className="shrink-0" aria-hidden>
      <circle cx="34" cy="34" r={r} fill="none" stroke="var(--yalola-border)" strokeWidth="6" />
      <circle cx="34" cy="34" r={r} fill="none" stroke="var(--yalola-primary)" strokeWidth="6" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c * (1 - percent / 100)} transform="rotate(-90 34 34)" style={{ transition: "stroke-dashoffset .6s ease" }} />
      <text x="34" y="38.5" textAnchor="middle" fontSize="13" fontWeight="700" fill="var(--yalola-primary)" data-testid="profile-completion-percent">{`${percent}%`}</text>
    </svg>
  );
};

// Carte « Complète ton profil » (lot 580) — épinglée en tête du fil et sur son profil tant que les éléments
// OBLIGATOIRES manquent (publier / commenter / aimer / suivre bloqués) ; ensuite « Plus tard » la masque jusqu'à 100 %.
// Comptes classiques / existants : `profile_completion` est absent → rien n'est affiché.
export const ProfileCompletionCard = ({ className = "" }) => {
  const { user, t } = useApp();
  const labels = useCompletionLabels();
  const pc = user?.profile_completion;
  const [open, setOpen] = useState(false);
  const [later, setLater] = useState(() => { try { return localStorage.getItem(DISMISS_KEY) === "1"; } catch { return false; } });
  // Un élément obligatoire qui redevient manquant ré-affiche la carte.
  useEffect(() => { if (pc && !pc.complete && later) { setLater(false); try { localStorage.removeItem(DISMISS_KEY); } catch {} } }, [pc, later]);
  if (!pc || pc.percent >= 100 || (pc.complete && later)) return null;
  const dismiss = () => { setLater(true); try { localStorage.setItem(DISMISS_KEY, "1"); } catch {} };
  const requiredLeft = pc.missing_required.length;

  return (
    <div className={`yalola-card p-4 ${className}`} data-testid="profile-completion-card" data-percent={pc.percent} data-complete={pc.complete ? "1" : "0"}>
      <div className="flex items-start gap-3">
        <Ring percent={pc.percent} />
        <div className="min-w-0 flex-1">
          <div className="font-bold font-display flex items-center gap-1.5">
            {!pc.complete && <Lock size={14} style={{ color: "var(--yalola-primary)" }} />}
            {t("profile_done.title", "Complète ton profil")}
          </div>
          <p className="text-sm mt-0.5" style={{ color: "var(--yalola-text-muted)" }} data-testid="profile-completion-subtitle">
            {pc.complete
              ? t("profile_done.optional_left", "Ajoute une photo et quelques mots pour atteindre 100 %.")
              : t("profile_done.required_left", "Encore {n} étape(s) obligatoire(s) pour publier, commenter, aimer et suivre").replace("{n}", String(requiredLeft))}
          </p>
          <ul className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1" data-testid="profile-completion-items">
            {pc.items.map((i) => (
              <li key={i.key} className="flex items-center gap-1.5 text-xs" style={{ color: i.done ? "var(--yalola-text-muted)" : "var(--yalola-text)" }} data-testid={`profile-completion-item-${i.key}`} data-done={i.done ? "1" : "0"}>
                {i.done ? <Check size={13} style={{ color: "#1B7A3D" }} /> : <Circle size={13} style={{ color: i.required ? "var(--yalola-primary)" : "var(--yalola-text-muted)" }} />}
                <span className={i.done ? "line-through" : ""}>{labels[i.key] || i.key}{!i.required && !i.done ? ` · ${t("onboarding.optional", "facultatif")}` : ""}</span>
              </li>
            ))}
          </ul>
          <div className="flex items-center flex-wrap gap-x-3 gap-y-2 mt-3">
            <Button className="pill-primary rounded-full h-10 px-5" onClick={() => setOpen(true)} data-testid="profile-completion-cta">
              {t("profile_done.cta", "Compléter maintenant (2 min)")}
            </Button>
            {pc.complete && <button type="button" onClick={dismiss} className="text-xs font-semibold whitespace-nowrap" style={{ color: "var(--yalola-text-muted)" }} data-testid="profile-completion-later">{t("bio_nudge.later", "Plus tard")}</button>}
          </div>
        </div>
      </div>
      <ProfileCompletionSheet open={open} onOpenChange={setOpen} />
    </div>
  );
};

// Garde globale (montée dans App) : une interaction refusée par le serveur (403 profile_incomplete) ouvre la feuille.
// Lot 589 : tant que le profil est bloqué, les dialogues natifs (ATT, âge, consentement pubs, notifications) attendent.
export const ProfileCompletionGate = () => {
  const { user } = useApp();
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const on = () => setOpen(true);
    window.addEventListener("yalola:profile_incomplete", on);
    return () => window.removeEventListener("yalola:profile_incomplete", on);
  }, []);
  const incomplete = profileIncomplete(user);
  useEffect(() => {
    if (incomplete) holdNativePrompts("profile_incomplete"); else releaseNativePrompts("profile_incomplete");
    return () => releaseNativePrompts("profile_incomplete");
  }, [incomplete]);
  if (!user?.profile_completion) return null;
  return <ProfileCompletionSheet open={open} onOpenChange={setOpen} reason="gate" />;
};
